import { first } from 'lodash-es';

import {
	ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnChanges, SimpleChanges
} from '@angular/core';

import { FADE_IN } from '@bp/shared/animations';
import { PaymentOptionType, PspPaymentOptionType, PaymentCardBrand } from '@bp/shared/models/business';
import { Destroyable } from '@bp/shared/models/common';
import { ICurrency } from '@bp/shared/models/currencies';
import { BpError } from '@bp/shared/models/core';
import { BridgerPsp, BridgerPspsByPaymentOptionTypeMap } from '@bp/shared/domains/bridger-psps/core';

@Component({
	selector: 'bp-psps-adding',
	templateUrl: './psps-adding.component.html',
	styleUrls: [ './psps-adding.component.scss' ],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [ FADE_IN ],
})
export class PspsAddingComponent extends Destroyable implements OnChanges {

	// eslint-disable-next-line @typescript-eslint/naming-convention
	PaymentOptionType = PaymentOptionType;

	@Input() pspsByPaymentOptionTypeMap!: BridgerPspsByPaymentOptionTypeMap | null;

	@Input() error: BpError | null = null;

	@Input() pspsLimit!: number | null;

	@Output() readonly hasSelectedPsps = new EventEmitter<boolean>();

	@Output() readonly selectedPspsCountChange = new EventEmitter<number>();

	@Output()
	readonly selectedPspsByPaymentOptionTypeMapChange = new EventEmitter<BridgerPspsByPaymentOptionTypeMap>();

	selectedPaymentOptionType = PspPaymentOptionType.creditCard;

	selectedPspsByPaymentOptionTypeMap = new Map<PspPaymentOptionType, BridgerPsp[]>();

	paymentOptionTypes: PspPaymentOptionType[] = [];

	pspsFilter: Partial<BridgerPsp> | null = null;

	availableCurrenciesPerPaymentOptionType = new Map<PspPaymentOptionType, Set<ICurrency>>();

	availableCreditCardBrands: Set<PaymentCardBrand> = new Set();

	get isCreditCardPaymentOptionTypeSelected(): boolean {
		return this.selectedPaymentOptionType === PspPaymentOptionType.creditCard;
	}

	selectedPspsCount = 0;

	ngOnChanges({ pspsByPaymentOptionTypeMap }: Partial<SimpleChanges>): void {
		if (pspsByPaymentOptionTypeMap) {
			this.paymentOptionTypes = [ ...(this.pspsByPaymentOptionTypeMap?.keys() ?? []) ];

			this.selectedPaymentOptionType = first(this.paymentOptionTypes) ?? PspPaymentOptionType.creditCard;

			this.availableCurrenciesPerPaymentOptionType
				= this._aggregateCurrenciesPerPaymentOptionTypeFromAllBridgerPsps();

			this.availableCreditCardBrands = this._aggregateCreditCardBrandsFromAllBridgerPsps();
		}
	}

	storeSelectedPaymentOptionPsps(optionType: PspPaymentOptionType, psps: Set<BridgerPsp>): void {
		this.selectedPspsByPaymentOptionTypeMap = new Map([
			...this.selectedPspsByPaymentOptionTypeMap,
			[ optionType, [ ...psps ]],
		]);

		this.selectedPspsByPaymentOptionTypeMapChange.emit(this.selectedPspsByPaymentOptionTypeMap);

		this.hasSelectedPsps.emit(this._checkIfHasSelectedPsps());

		this.selectedPspsCountChange.emit(this._countSelectedPsps());
	}

	private _checkIfHasSelectedPsps(): boolean {
		return [ ...this.selectedPspsByPaymentOptionTypeMap.keys() ]
			.some(option => this.selectedPspsByPaymentOptionTypeMap.get(option)?.length);
	}

	private _countSelectedPsps(): number {
	 	this.selectedPspsCount = [ ...this.selectedPspsByPaymentOptionTypeMap.values() ]
			.flat()
			.length;

		return this.selectedPspsCount;
	}

	private _aggregateCurrenciesPerPaymentOptionTypeFromAllBridgerPsps(): Map<PspPaymentOptionType, Set<ICurrency>> {
		return new Map(
			[ ...(this.pspsByPaymentOptionTypeMap?.entries() ?? []) ]
				.map(([ paymentOptionType, bridgerPsps ]) => [
					paymentOptionType,
					new Set(bridgerPsps.flatMap(
						bridgerPsp => <ICurrency[]> (bridgerPsp.paymentOptionByTypeMap.get(paymentOptionType)?.currencies ?? []),
					)),
				]),
		);
	}

	private _aggregateCreditCardBrandsFromAllBridgerPsps(): Set<PaymentCardBrand> {
		return new Set([ ...(this.pspsByPaymentOptionTypeMap?.values() ?? []) ]
			.flatMap(bridgerPsps => bridgerPsps.flatMap(
				bridgerPsp => bridgerPsp.paymentCardBrands,
			)));
	}

}
